/** @format */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableHead,
    TableRow,
    MenuItem,
    Select,
    CircularProgress,
    Chip,
    Stack,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
} from "@mui/material";
import {
    Refresh,
    AssignmentTurnedInOutlined,
    PlayArrowOutlined,
    CancelOutlined,
    AddOutlined,
    OpenInNew,
} from "@mui/icons-material";

import { selectActiveBusinessId } from "../../redux/selectors/collectSelector";
import { selectCurrentUser } from "../../redux/selectors/userSelector";
import {
    fetchTasks,
    fetchTasksSummary,
    claimTaskApi,
    completeTaskApi,
    cancelTaskApi,
    createTask,
    ITask,
    ITasksSummary,
} from "../../redux/services/collectTaskService";
import { useToast } from "../../providers/ToastProvider";
import {
    CollectPageWrapper,
    CollectSectionCard,
    CollectSectionHeader,
    CollectSectionTitle,
    CollectSectionSubtitle,
    CollectTableContainer,
    CollectTableHeadCell,
    CollectTableBodyCell,
    CollectEmptyState,
    CollectEmptyTitle,
    CollectEmptyHint,
    CollectPrimaryButton,
    CollectGhostButton,
    CollectKpiCard,
    CollectKpiLabel,
    CollectKpiValue,
    CollectKpiHint,
    FlexBetween,
} from "./styles";

// ─────────────────────────────────────────────────────────────────────────
// CollectTasks (Phase 2E) — actionable inbox for collections operators.
// Tasks are auto-created by the recovery engine (manual_review, broken
// promise, dispute, low-confidence reply) and can also be created
// manually. Each row supports claim / complete / cancel actions.
// ─────────────────────────────────────────────────────────────────────────

const PRIORITY_ORDER = ["urgent", "high", "medium", "low"];
const PRIORITY_COLORS: Record<string, string> = {
    urgent: "#dc2626",
    high: "#f59e0b",
    medium: "#0ea5e9",
    low: "#94a3b8",
};
const STATUS_COLORS: Record<string, string> = {
    open: "#0ea5e9",
    in_progress: "#f59e0b",
    done: "#16a34a",
    cancelled: "#94a3b8",
};

const TYPE_LABELS: Record<string, string> = {
    manual_review: "Manual review",
    review_dispute: "Dispute",
    review_complaint: "Complaint",
    verify_payment_claim: "Verify payment",
    broken_promise_followup: "Broken promise",
    legal_action_review: "Legal action",
    review_reply: "Review reply",
    custom: "Custom",
};

function formatDate(v?: string): string {
    if (!v) return "—";
    const d = new Date(v);
    if (isNaN(d.getTime())) return "—";
    try {
        return d.toLocaleString();
    } catch {
        return v;
    }
}

function assigneeName(t: ITask): string {
    const a = t.assignedToUserId;
    if (!a) return "Unassigned";
    if (typeof a === "string") return "Assigned";
    const first = a.firstName?.trim() || "";
    const last = a.lastName?.trim() || "";
    if (first || last) return `${first} ${last}`.trim();
    return a.email || "Assigned";
}

const CollectTasks: React.FC = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const businessId = useSelector(selectActiveBusinessId);
    const currentUser = useSelector(selectCurrentUser);

    const [tasks, setTasks] = useState<ITask[]>([]);
    const [summary, setSummary] = useState<ITasksSummary | null>(null);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Filters
    const [statusFilter, setStatusFilter] = useState<string>("open,in_progress");
    const [assigneeFilter, setAssigneeFilter] = useState<string>("any");
    const [typeFilter, setTypeFilter] = useState<string>("all");

    // Create dialog
    const [createOpen, setCreateOpen] = useState(false);
    const [createTitle, setCreateTitle] = useState("");
    const [createDescription, setCreateDescription] = useState("");
    const [createPriority, setCreatePriority] = useState("medium");
    const [creating, setCreating] = useState(false);

    // Per-row pending actions
    const [pendingId, setPendingId] = useState<string | null>(null);

    const load = useCallback(async () => {
        if (!businessId) return;
        setLoading(true);
        setError(null);
        try {
            const status = statusFilter.split(",").filter(Boolean);
            const params: any = { status, limit: 100 };
            if (assigneeFilter === "me") params.assignedToUserId = "me";
            else if (assigneeFilter === "unassigned")
                params.assignedToUserId = "unassigned";
            if (typeFilter !== "all") params.type = [typeFilter];
            const [page, sum] = await Promise.all([
                fetchTasks(businessId, params),
                fetchTasksSummary(businessId),
            ]);
            setTasks(page.items || []);
            setTotal(page.total || 0);
            setSummary(sum);
        } catch (err: any) {
            setError(err.message || "Could not load tasks");
        } finally {
            setLoading(false);
        }
    }, [businessId, statusFilter, assigneeFilter, typeFilter]);

    useEffect(() => {
        load();
    }, [load]);

    const handleClaim = async (taskId: string) => {
        if (!businessId) return;
        setPendingId(taskId);
        try {
            await claimTaskApi(businessId, taskId);
            toast.success("Task claimed");
            await load();
        } catch (err: any) {
            toast.error(err.message || "Could not claim task");
        } finally {
            setPendingId(null);
        }
    };

    const handleComplete = async (taskId: string) => {
        if (!businessId) return;
        setPendingId(taskId);
        try {
            await completeTaskApi(businessId, taskId);
            toast.success("Task completed");
            await load();
        } catch (err: any) {
            toast.error(err.message || "Could not complete task");
        } finally {
            setPendingId(null);
        }
    };

    const handleCancel = async (taskId: string) => {
        if (!businessId) return;
        setPendingId(taskId);
        try {
            await cancelTaskApi(businessId, taskId);
            toast.info("Task cancelled");
            await load();
        } catch (err: any) {
            toast.error(err.message || "Could not cancel task");
        } finally {
            setPendingId(null);
        }
    };

    const handleCreate = async () => {
        if (!businessId) return;
        if (!createTitle.trim()) {
            toast.warning("Please add a task title");
            return;
        }
        setCreating(true);
        try {
            await createTask(businessId, {
                title: createTitle.trim(),
                description: createDescription.trim() || undefined,
                priority: createPriority,
                type: "custom",
            });
            toast.success("Task created");
            setCreateOpen(false);
            setCreateTitle("");
            setCreateDescription("");
            setCreatePriority("medium");
            await load();
        } catch (err: any) {
            toast.error(err.message || "Could not create task");
        } finally {
            setCreating(false);
        }
    };

    const sorted = useMemo(() => {
        return [...tasks].sort((a, b) => {
            const pa = PRIORITY_ORDER.indexOf(a.priority);
            const pb = PRIORITY_ORDER.indexOf(b.priority);
            if (pa !== pb) return pa - pb;
            const da = new Date(a.createdAt || 0).getTime();
            const db = new Date(b.createdAt || 0).getTime();
            return db - da;
        });
    }, [tasks]);

    return (
        <CollectPageWrapper>
            <Box sx={{ maxWidth: 1200, mx: "auto", px: { xs: 2, md: 3 }, py: 3 }}>
                <FlexBetween sx={{ mb: 2 }}>
                    <Box>
                        <Typography
                            variant="h5"
                            sx={{
                                fontFamily: "'Montserrat', sans-serif",
                                fontWeight: 800,
                                color: "#0f172a",
                            }}
                        >
                            Tasks
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: "'Montserrat', sans-serif",
                                color: "#64748b",
                                fontSize: "0.9rem",
                            }}
                        >
                            Things that need a human touch manual reviews,
                            broken promises, disputes, and follow-ups.
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                        <CollectGhostButton
                            startIcon={<Refresh fontSize="small" />}
                            onClick={load}
                            disabled={loading}
                        >
                            Refresh
                        </CollectGhostButton>
                        <CollectPrimaryButton
                            startIcon={<AddOutlined fontSize="small" />}
                            onClick={() => setCreateOpen(true)}
                        >
                            New task
                        </CollectPrimaryButton>
                    </Stack>
                </FlexBetween>

                {/* KPIs */}
                {summary && (
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "1fr 1fr",
                                sm: "repeat(4, 1fr)",
                            },
                            gap: 2,
                            mb: 3,
                        }}
                    >
                        <CollectKpiCard>
                            <CollectKpiLabel>Open</CollectKpiLabel>
                            <CollectKpiValue>{summary.open}</CollectKpiValue>
                            <CollectKpiHint>waiting to be picked up</CollectKpiHint>
                        </CollectKpiCard>
                        <CollectKpiCard>
                            <CollectKpiLabel>In progress</CollectKpiLabel>
                            <CollectKpiValue>{summary.inProgress}</CollectKpiValue>
                            <CollectKpiHint>currently assigned</CollectKpiHint>
                        </CollectKpiCard>
                        <CollectKpiCard>
                            <CollectKpiLabel>Completed</CollectKpiLabel>
                            <CollectKpiValue>{summary.done}</CollectKpiValue>
                            <CollectKpiHint>resolved</CollectKpiHint>
                        </CollectKpiCard>
                        <CollectKpiCard>
                            <CollectKpiLabel>Cancelled</CollectKpiLabel>
                            <CollectKpiValue>{summary.cancelled}</CollectKpiValue>
                            <CollectKpiHint>dismissed</CollectKpiHint>
                        </CollectKpiCard>
                    </Box>
                )}

                <CollectSectionCard>
                    <CollectSectionHeader>
                        <Box>
                            <CollectSectionTitle>
                                {total > 0
                                    ? `${sorted.length} task${sorted.length === 1 ? "" : "s"}`
                                    : "Tasks"}
                            </CollectSectionTitle>
                            <CollectSectionSubtitle>
                                Highest priority shown first
                            </CollectSectionSubtitle>
                        </Box>
                        <Stack direction="row" spacing={1.5} flexWrap="wrap">
                            <FormControl size="small" sx={{ minWidth: 160 }}>
                                <InputLabel>Status</InputLabel>
                                <Select
                                    label="Status"
                                    value={statusFilter}
                                    onChange={(e) =>
                                        setStatusFilter(String(e.target.value))
                                    }
                                >
                                    <MenuItem value="open,in_progress">
                                        Active (open + in progress)
                                    </MenuItem>
                                    <MenuItem value="open">Open only</MenuItem>
                                    <MenuItem value="in_progress">
                                        In progress only
                                    </MenuItem>
                                    <MenuItem value="done">Completed</MenuItem>
                                    <MenuItem value="cancelled">Cancelled</MenuItem>
                                    <MenuItem value="open,in_progress,done,cancelled">
                                        All
                                    </MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl size="small" sx={{ minWidth: 160 }}>
                                <InputLabel>Assignee</InputLabel>
                                <Select
                                    label="Assignee"
                                    value={assigneeFilter}
                                    onChange={(e) =>
                                        setAssigneeFilter(String(e.target.value))
                                    }
                                >
                                    <MenuItem value="any">Anyone</MenuItem>
                                    <MenuItem value="me">Assigned to me</MenuItem>
                                    <MenuItem value="unassigned">
                                        Unassigned
                                    </MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl size="small" sx={{ minWidth: 160 }}>
                                <InputLabel>Type</InputLabel>
                                <Select
                                    label="Type"
                                    value={typeFilter}
                                    onChange={(e) =>
                                        setTypeFilter(String(e.target.value))
                                    }
                                >
                                    <MenuItem value="all">All types</MenuItem>
                                    {Object.entries(TYPE_LABELS).map(([k, v]) => (
                                        <MenuItem key={k} value={k}>
                                            {v}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Stack>
                    </CollectSectionHeader>

                    {loading ? (
                        <Box sx={{ p: 4, textAlign: "center" }}>
                            <CircularProgress size={28} />
                        </Box>
                    ) : error ? (
                        <CollectEmptyState>
                            <CollectEmptyTitle>
                                Couldn&apos;t load tasks
                            </CollectEmptyTitle>
                            <CollectEmptyHint>{error}</CollectEmptyHint>
                        </CollectEmptyState>
                    ) : sorted.length === 0 ? (
                        <CollectEmptyState>
                            <CollectEmptyTitle>No tasks here</CollectEmptyTitle>
                            <CollectEmptyHint>
                                You&apos;re all caught up. New tasks will appear
                                automatically when the system needs your help.
                            </CollectEmptyHint>
                        </CollectEmptyState>
                    ) : (
                        <CollectTableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <CollectTableHeadCell>
                                            Title
                                        </CollectTableHeadCell>
                                        <CollectTableHeadCell>
                                            Type
                                        </CollectTableHeadCell>
                                        <CollectTableHeadCell>
                                            Priority
                                        </CollectTableHeadCell>
                                        <CollectTableHeadCell>
                                            Status
                                        </CollectTableHeadCell>
                                        <CollectTableHeadCell>
                                            Assignee
                                        </CollectTableHeadCell>
                                        <CollectTableHeadCell>
                                            Created
                                        </CollectTableHeadCell>
                                        <CollectTableHeadCell align="right">
                                            Actions
                                        </CollectTableHeadCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {sorted.map((t) => {
                                        const isOpen = t.status === "open";
                                        const isInProgress =
                                            t.status === "in_progress";
                                        const isActive = isOpen || isInProgress;
                                        const myUserId = (currentUser as any)?._id;
                                        const assignee = t.assignedToUserId;
                                        const assigneeId =
                                            typeof assignee === "string"
                                                ? assignee
                                                : assignee?._id;
                                        const mine =
                                            myUserId && assigneeId === myUserId;
                                        return (
                                            <TableRow
                                                key={t._id}
                                                hover
                                                sx={{
                                                    cursor: t.invoiceId
                                                        ? "pointer"
                                                        : "default",
                                                }}
                                                onClick={() => {
                                                    if (t.invoiceId)
                                                        navigate(
                                                            `/collect/invoices/${t.invoiceId}`,
                                                        );
                                                }}
                                            >
                                                <CollectTableBodyCell>
                                                    <Typography
                                                        sx={{
                                                            fontFamily:
                                                                "'Montserrat', sans-serif",
                                                            fontWeight: 600,
                                                            fontSize: "0.85rem",
                                                            color: "#0f172a",
                                                        }}
                                                    >
                                                        {t.title}
                                                    </Typography>
                                                    {t.description && (
                                                        <Typography
                                                            sx={{
                                                                fontFamily:
                                                                    "'Montserrat', sans-serif",
                                                                color: "#64748b",
                                                                fontSize:
                                                                    "0.75rem",
                                                                mt: 0.25,
                                                            }}
                                                        >
                                                            {t.description}
                                                        </Typography>
                                                    )}
                                                </CollectTableBodyCell>
                                                <CollectTableBodyCell>
                                                    {TYPE_LABELS[t.type] || t.type}
                                                </CollectTableBodyCell>
                                                <CollectTableBodyCell>
                                                    <Chip
                                                        label={t.priority}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor:
                                                                PRIORITY_COLORS[
                                                                t.priority
                                                                ] || "#94a3b8",
                                                            color: "#fff",
                                                            textTransform:
                                                                "capitalize",
                                                            fontWeight: 600,
                                                        }}
                                                    />
                                                </CollectTableBodyCell>
                                                <CollectTableBodyCell>
                                                    <Chip
                                                        label={t.status.replace(
                                                            "_",
                                                            " ",
                                                        )}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor:
                                                                STATUS_COLORS[
                                                                t.status
                                                                ] || "#94a3b8",
                                                            color: "#fff",
                                                            textTransform:
                                                                "capitalize",
                                                            fontWeight: 600,
                                                        }}
                                                    />
                                                </CollectTableBodyCell>
                                                <CollectTableBodyCell>
                                                    {assigneeName(t)}
                                                </CollectTableBodyCell>
                                                <CollectTableBodyCell>
                                                    {formatDate(t.createdAt)}
                                                </CollectTableBodyCell>
                                                <CollectTableBodyCell align="right">
                                                    <Stack
                                                        direction="row"
                                                        spacing={0.5}
                                                        justifyContent="flex-end"
                                                        onClick={(e) =>
                                                            e.stopPropagation()
                                                        }
                                                    >
                                                        {isOpen && !mine && (
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                startIcon={
                                                                    <PlayArrowOutlined fontSize="small" />
                                                                }
                                                                disabled={
                                                                    pendingId ===
                                                                    t._id
                                                                }
                                                                onClick={() =>
                                                                    handleClaim(
                                                                        t._id,
                                                                    )
                                                                }
                                                            >
                                                                Claim
                                                            </Button>
                                                        )}
                                                        {isActive && (
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                color="success"
                                                                startIcon={
                                                                    <AssignmentTurnedInOutlined fontSize="small" />
                                                                }
                                                                disabled={
                                                                    pendingId ===
                                                                    t._id
                                                                }
                                                                onClick={() =>
                                                                    handleComplete(
                                                                        t._id,
                                                                    )
                                                                }
                                                            >
                                                                Complete
                                                            </Button>
                                                        )}
                                                        {isActive && (
                                                            <Button
                                                                size="small"
                                                                variant="text"
                                                                color="warning"
                                                                startIcon={
                                                                    <CancelOutlined fontSize="small" />
                                                                }
                                                                disabled={
                                                                    pendingId ===
                                                                    t._id
                                                                }
                                                                onClick={() =>
                                                                    handleCancel(
                                                                        t._id,
                                                                    )
                                                                }
                                                            >
                                                                Cancel
                                                            </Button>
                                                        )}
                                                        {t.invoiceId && (
                                                            <Button
                                                                size="small"
                                                                variant="text"
                                                                startIcon={
                                                                    <OpenInNew fontSize="small" />
                                                                }
                                                                onClick={() =>
                                                                    navigate(
                                                                        `/collect/invoices/${t.invoiceId}`,
                                                                    )
                                                                }
                                                            >
                                                                Open
                                                            </Button>
                                                        )}
                                                    </Stack>
                                                </CollectTableBodyCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </CollectTableContainer>
                    )}
                </CollectSectionCard>
            </Box>

            {/* Create task dialog */}
            <Dialog
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Create a new task</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label="Title"
                            value={createTitle}
                            onChange={(e) => setCreateTitle(e.target.value)}
                            fullWidth
                            autoFocus
                        />
                        <TextField
                            label="Description (optional)"
                            value={createDescription}
                            onChange={(e) =>
                                setCreateDescription(e.target.value)
                            }
                            fullWidth
                            multiline
                            minRows={3}
                        />
                        <FormControl size="small">
                            <InputLabel>Priority</InputLabel>
                            <Select
                                label="Priority"
                                value={createPriority}
                                onChange={(e) =>
                                    setCreatePriority(String(e.target.value))
                                }
                            >
                                <MenuItem value="urgent">Urgent</MenuItem>
                                <MenuItem value="high">High</MenuItem>
                                <MenuItem value="medium">Medium</MenuItem>
                                <MenuItem value="low">Low</MenuItem>
                            </Select>
                        </FormControl>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleCreate}
                        disabled={creating}
                    >
                        {creating ? "Creating…" : "Create task"}
                    </Button>
                </DialogActions>
            </Dialog>
        </CollectPageWrapper>
    );
};

export default CollectTasks;
